import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Space,
  Input,
  Select,
  Button,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  message,
  Popconfirm,
  Typography,
  Spin,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  saasControlService,
  SaasPayment,
  SaasPaymentType,
  CreatePaymentRequest,
} from "~/services/saas-control";
import dayjs from "dayjs";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export const PaymentsTab: React.FC = () => {
  const [payments, setPayments] = useState<SaasPayment[]>([]);
  const [allClients, setAllClients] = useState<{ id: string; companyName: string; email: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | undefined>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPayment, setEditingPayment] = useState<SaasPayment | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [paymentsData, clientsData] = await Promise.all([
        saasControlService.getPayments(),
        saasControlService.getAllClientsForPayment(),
      ]);
      setPayments(paymentsData);
      setAllClients(clientsData);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPayment(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (payment: SaasPayment) => {
    setEditingPayment(payment);
    form.setFieldsValue({
      ...payment,
      paymentDate: dayjs(payment.paymentDate),
      billingPeriodStart: payment.billingPeriodStart
        ? dayjs(payment.billingPeriodStart)
        : undefined,
      billingPeriodEnd: payment.billingPeriodEnd
        ? dayjs(payment.billingPeriodEnd)
        : undefined,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await saasControlService.deletePayment(id);
      message.success("Payment deleted successfully");
      loadData();
    } catch (error) {
      message.error("Failed to delete payment");
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);
      const data: CreatePaymentRequest = {
        ...values,
        paymentDate: values.paymentDate.toISOString(),
        billingPeriodStart: values.billingPeriodStart?.toISOString(),
        billingPeriodEnd: values.billingPeriodEnd?.toISOString(),
      };

      if (editingPayment) {
        await saasControlService.updatePayment(editingPayment.id, data);
        message.success("Payment updated successfully");
      } else {
        await saasControlService.createPayment(data);
        message.success("Payment created successfully");
      }

      setIsModalVisible(false);
      loadData();
    } catch (error) {
      message.error("Failed to save payment");
    } finally {
      setSubmitting(false);
    }
  };

  const getTypeColor = (type: SaasPaymentType) => {
    switch (type) {
      case SaasPaymentType.SUBSCRIPTION:
        return "blue";
      case SaasPaymentType.MANUAL:
        return "green";
      case SaasPaymentType.SERVICE_CREDIT:
        return "purple";
      default:
        return "default";
    }
  };

  const getTypeLabel = (type: SaasPaymentType) => {
    switch (type) {
      case SaasPaymentType.SUBSCRIPTION:
        return "Subscription";
      case SaasPaymentType.MANUAL:
        return "Manual";
      case SaasPaymentType.SERVICE_CREDIT:
        return "Service Credit";
      default:
        return type;
    }
  };

  const columns: ColumnsType<SaasPayment> = [
    {
      title: "Customer",
      key: "client",
      render: (_, record) => (
        <Space>
          <ShopOutlined />
          <div>
            <div className="font-medium">{record.client.companyName}</div>
            <Text type="secondary" className="text-xs">
              {record.client.email || "No email"}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Tag color={getTypeColor(type)}>{getTypeLabel(type)}</Tag>
      ),
      filters: [
        { text: "Subscription", value: SaasPaymentType.SUBSCRIPTION },
        { text: "Manual", value: SaasPaymentType.MANUAL },
        { text: "Service Credit", value: SaasPaymentType.SERVICE_CREDIT },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (
        <Text strong className="text-green-600">
          ${Number(amount).toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => Number(a.amount) - Number(b.amount),
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (date) => dayjs(date).format("MMM D, YYYY"),
      sorter: (a, b) =>
        new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime(),
      defaultSortOrder: "descend",
    },
    {
      title: "Billing Period",
      key: "billingPeriod",
      render: (_, record) =>
        record.billingPeriodStart && record.billingPeriodEnd ? (
          <Text type="secondary" className="text-xs">
            {dayjs(record.billingPeriodStart).format("MMM D")} -{" "}
            {dayjs(record.billingPeriodEnd).format("MMM D, YYYY")}
          </Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (desc) => desc || <Text type="secondary">-</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this payment?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredPayments = payments.filter((payment) => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      payment.client.companyName.toLowerCase().includes(searchLower) ||
      payment.client.email?.toLowerCase().includes(searchLower) ||
      payment.description?.toLowerCase().includes(searchLower);

    const matchesType = typeFilter === undefined || payment.type === typeFilter;

    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4 flex justify-between items-center">
        <div className="flex gap-4">
          <Input
            placeholder="Search payments..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 300 }}
          />
          <Select
            placeholder="Filter by type"
            value={typeFilter}
            onChange={setTypeFilter}
            allowClear
            style={{ width: 150 }}
          >
            <Option value={SaasPaymentType.SUBSCRIPTION}>Subscription</Option>
            <Option value={SaasPaymentType.MANUAL}>Manual</Option>
            <Option value={SaasPaymentType.SERVICE_CREDIT}>
              Service Credit
            </Option>
          </Select>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Add Payment
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={filteredPayments}
        rowKey="id"
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} payments`,
        }}
      />

      <Modal
        title={editingPayment ? "Edit Payment" : "Add New Payment"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            type: SaasPaymentType.MANUAL,
            paymentDate: dayjs(),
          }}
        >
          {!editingPayment && (
            <Form.Item
              name="clientId"
              label="Customer"
              rules={[{ required: true, message: "Please select a customer" }]}
            >
              <Select
                showSearch
                placeholder="Select a customer"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {allClients.map((client) => (
                  <Option key={client.id} value={client.id}>
                    {client.companyName} ({client.email || "No email"})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="type"
            label="Payment Type"
            rules={[{ required: true, message: "Please select a type" }]}
          >
            <Select>
              <Option value={SaasPaymentType.SUBSCRIPTION}>Subscription</Option>
              <Option value={SaasPaymentType.MANUAL}>Manual</Option>
              <Option value={SaasPaymentType.SERVICE_CREDIT}>
                Service Credit
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true, message: "Please enter an amount" }]}
          >
            <InputNumber
              prefix="$"
              min={0}
              precision={2}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            name="paymentDate"
            label="Payment Date"
            rules={[{ required: true, message: "Please select a date" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input placeholder="e.g., Monthly subscription payment" />
          </Form.Item>

          <Space style={{ width: "100%" }} size="large">
            <Form.Item
              name="billingPeriodStart"
              label="Billing Period Start"
              style={{ flex: 1 }}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="billingPeriodEnd"
              label="Billing Period End"
              style={{ flex: 1 }}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Space>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Additional notes..." />
          </Form.Item>

          <Form.Item className="mb-0">
            <Space className="w-full justify-end">
              <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingPayment ? "Update" : "Create"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
